import { normalizeId } from './ids';

/**
 * Card actions on the item menu (KPR-551). The pure parts: which buttons fit a
 * record, an input as the element the form renderer reads, the sentences a
 * press answers with.
 */

/** The buttons whose `appliesTo` holds this record's card (the API already limited them to what the caller can write). */
export function actionsForCard(list, cardId) {
	const id = normalizeId(cardId);
	if (!id || !Array.isArray(list)) return [];
	return list.filter(row => row?._id && Array.isArray(row.appliesTo) && row.appliesTo.some(card => normalizeId(card) === id));
}

/** Does any of these actions apply only to some records (KPR-1342)? Then the record's own answer is worth asking. */
export const anyConditional = rows => Array.isArray(rows) && rows.some(row => row?.conditional === true);

/**
 * Where one action stands on one record (KPR-1342), from the record's own read
 * (`answers`: a Map of action id to `{ applies, reason }`, or null while it is
 * not known): `ready` (press it), `unavailable` with the API's reason, or
 * `checking` while a conditioned one is not answered yet. An action without a
 * condition is always ready; one the answer does not list, or an answer that
 * failed, is ready too — the press asks the server again either way.
 */
export function actionStanding(row, answers, { failed = false } = {}) {
	if (!row?.conditional) return { state: 'ready' };
	const id = normalizeId(row._id);
	if (answers && answers.has(id)) {
		const answer = answers.get(id);
		if (answer.applies === false) return { state: 'unavailable', reason: answer.reason || 'Not available for this record' };
		return { state: 'ready' };
	}
	if (failed || answers) return { state: 'ready' };
	return { state: 'checking' };
}

/** A press the API refused because the action does not apply (or no longer can): the record's answer is out of date. */
export const isNotApplicable = error => {
	const code = error?.response?.data?.code;
	return code === 'action_not_applicable' || code === 'action_broken';
};

/** The text on a button: its label, else the action's name. */
export const actionLabel = row => String(row?.button?.label || row?.name || 'Run action').trim();

/** One press input as the element definition the form renderer reads. */
export const inputAsElement = input => ({
	name: input.key,
	label: { singular: input.label || input.key, plural: input.label || input.key },
	dataType: input.dataType,
	options: input.options || {},
});

/** A fresh Idempotency-Key: one per press attempt (a refused press spends its key). */
export function newPressKey() {
	const c = typeof window !== 'undefined' ? window.crypto : null;
	if (c && typeof c.randomUUID === 'function') return c.randomUUID();
	return `press-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * What a press wrote, from the API's `written`: how many records changed,
 * and the records it created (id and card) so the toast can link them.
 */
export function summarizeWritten(answer) {
	const written = Array.isArray(answer?.written) ? answer.written : [];
	const updated = new Set();
	const created = [];
	written.forEach(entry => {
		const id = normalizeId(entry?._id);
		if (!id) return;
		if (entry.op === 'created') created.push({ id, cardId: normalizeId(entry.card_id) });
		else updated.add(id);
	});
	return { updated: updated.size, created };
}

/** "Changed 1 item." / "Nothing needed changing." / "Changed 2 items and created 1 item." for the lead of the toast. */
export function pressSentence(answer) {
	const { updated, created } = summarizeWritten(answer);
	if (!updated && !created.length) return 'Nothing needed changing.';
	const parts = [];
	if (updated) parts.push(`changed ${plural(updated, 'item', 'items')}`);
	if (created.length) parts.push(`created ${plural(created.length, 'item', 'items')}`);
	const text = parts.join(' and ');
	return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
}

/**
 * A refused press in the API's own plain words. Its message is written for a
 * person (a lock, a missing right, the owner's limit); only a failure with no
 * answer at all gets ours.
 */
export function pressRefusal(error) {
	const data = error?.response?.data;
	if (data && typeof data.message === 'string' && data.message.trim()) return data.message.trim();
	if (data && typeof data.error === 'string' && data.error.trim()) return data.error.trim();
	if (error?.response) return 'That could not be done. Nothing was changed.';
	return 'That could not be reached. Check your connection and try again.';
}

/** A partial press (a race stopped it part-way): why it stopped and what landed. */
export function partialSentence(answer) {
	const reason = answer?.failedAt?.message || 'A change was made while it ran.';
	const { updated, created } = summarizeWritten(answer);
	const done = updated + created.length;
	return `${reason} ${done ? `${plural(done, 'item was', 'items were')} changed before it stopped.` : 'Nothing was changed.'}`;
}
